import {
  ChangeDetectionStrategy,
  Component,
  effect,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  Subscription,
  take,
} from 'rxjs';

import { runtimeActions } from '@blaze-runtime/engine-data-access';
import {
  findByRefIds,
  FormContainerVariantType,
  getRootElementId,
  RuntimeContainerActionModel,
  WrapperField,
} from '@blaze-runtime/engine-domain';
import {
  ContainerElementMetaUiFeatures,
  DEFAULT_NO_DATA_MESSAGE,
  ElementOutputUpdateSource,
  getActiveBinding,
  getBackground,
  handleCustomFieldsSchema,
  makeBorderStyles,
  makeContainerVisualEffectStyles,
  RenderElementContext,
} from '@blaze-shared/elements-domain';
import { BlazeSchema, DatasetRow, type ElementSchema } from '@blaze-shared/schema-domain';
import { safeDetectChanges } from '@blaze-shared/shared-ui';
import { isDeepEqual, isEmptyish, useEmitTracker } from '@blaze-shared/shared-util';

import { getElementById } from '../../+state/elements/elements.selectors';
import {
  arePropsWithData,
  arePropsWithWrapperRefs,
  hasSlots,
} from '../../canvas-elements/element-model/element-props-guards';
import { isEditableForm } from '../../canvas-elements/element-model/element-type-guards';
import { FormRenderElementInstance } from '../../canvas-elements/models/render-element.models';
import { FormStateService, FormValidationService } from '../../services/forms/runtime/public-api';
import { RowGenerationService } from '../../services/row-generation.service';
import { shouldShowEmptyDataMessage } from '../../utils/elements/element.utils';
import { getFormElementCustomFields } from '../../utils/elements/form.utils';
import { CanvasElementBaseFormlikeComponent } from '../__base/base-formlike.component';
import { ElementDataActions } from '../__base/element-data-actions';

@UntilDestroy()
@Component({
  selector: 'be-form-container',
  templateUrl: './form-container.component.html',
  styleUrls: ['./form-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [ElementDataActions<FormContainerVariantType>],
})
export class FormContainerComponent
  extends CanvasElementBaseFormlikeComponent<FormContainerVariantType>
  implements OnInit, OnChanges, OnDestroy, FormRenderElementInstance
{
  private readonly formStateService = inject(FormStateService);
  private readonly formValidation = inject(FormValidationService);
  private readonly rowGenerationService = inject(RowGenerationService);
  protected readonly elementDataActions = inject(ElementDataActions);

  @Input() value: DatasetRow | undefined;

  @HostBinding('attr.data-validation-ready') validationReady?: boolean;

  private readonly outputEmitTracker = useEmitTracker();
  private readonly validateEmitTracker = useEmitTracker();
  private readonly dataValue = this.getDataInput('value');
  private valueProps = {
    initialized: false,
    preInitValues: undefined as Record<string, any> | undefined,
  };
  private isDialogChild?: boolean;

  readonly elementActionsKeyRoot = 'b-fac';
  elementActionsKey!: string;
  noBindingMessage$: Observable<string | undefined>;
  visibleHeaderActions$: Observable<RuntimeContainerActionModel[]> = of([]);
  showNoDataPanel = false;
  scrollToError = signal(false);
  elementErrors: Record<string, string> = {};

  labelEnabled!: boolean;
  labelText!: string;
  noDataMessage!: string;
  headerStyles!: Record<string, string | undefined>;
  wrapperStyles!: Record<string, string | undefined>;
  slotStyles!: Record<string, string | undefined>;
  noDataStyles!: Record<string, string | undefined>;

  get slotElementContext(): { elementContext: RenderElementContext } {
    return {
      elementContext: {
        extendInputPadding: this.elementType === 'formContainer',
        disableShadowEffect: true,
        dataContainer: 'form',
      },
    };
  }

  get schema(): ElementSchema | undefined {
    return this.getEventSchema('change');
  }

  constructor() {
    super();

    this.elementDataActions.props = toSignal(this.props$);
    this.elementDataActions.dataValue = this.dataValue;
    this.elementDataActions.elementId = toSignal(this.elementId$);

    this.dataValue.schema$.pipe(untilDestroyed(this)).subscribe((schema) => {
      this.updateSchemas(schema);
    });

    this.noBindingMessage$ = this.element$.pipe(
      map((element) => {
        if (!this.isInEditor || !hasSlots(element)) {
          return;
        }
        const hasOneSlot = element.slots.ids.length === 1;
        const firstSlotId = element.slots.ids[0];
        if (
          !hasOneSlot ||
          firstSlotId === undefined ||
          element.slots[firstSlotId]?.children.length !== 0
        ) {
          return;
        }
        return isEditableForm(element)
          ? 'To generate a form, choose an action from the properties panel.'
          : 'To generate a view, choose a data source from the properties panel.';
      }),
    );

    this.initMarginHandler((props) => props?.style?.container?.offset?.margin);

    this.setupScrollToError();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['elementId']) {
      this.elementActionsKey ||= `${this.elementActionsKeyRoot}-${this.elementId}`;
      if (this.ns) {
        /**
         * State will emit first value synchronously.
         */
        this.store
          .select(getElementById(getRootElementId(this.ns)))
          .pipe(take(1))
          .subscribe((parentElement) => {
            this.isDialogChild = parentElement?.type === 'dialog';
          });
      }
    }
    if (changes['props']) {
      const { content } = this.props ?? {};
      const { title, noDataMessage } = content || {};

      this.labelEnabled = title?.enabled ?? true;
      const scrollToError = content?.options?.scrollToError ?? false;
      if (scrollToError !== this.scrollToError()) {
        this.scrollToError.set(scrollToError);
      }
      this.noDataMessage = shouldShowEmptyDataMessage(this.props)
        ? (noDataMessage ?? DEFAULT_NO_DATA_MESSAGE)
        : '';

      this.updateStyles();

      if (!arePropsWithData(this.props) || !getActiveBinding(this.props?.data, 'value')) {
        this.updateSchemas();
        this.value = {};
      }

      // NOTE the model is not bound -> create obj from props model and emit updated value
      if (!this.bindingResults?.['value'] && !this.isDialogChild) {
        this.value = this.props?.content.model?.reduce((acc, curr) => {
          acc[curr.field] = curr.value;
          return acc;
        }, {} as any);
        this.updateOutput('initial');
        return;
      }
    }
  }

  ngOnInit(): void {
    this.elementDataActions.elementType = this.elementType;

    const headerActions$ = this.props$.pipe(
      map((props) => props?.content.headerActions),
      distinctUntilChanged(isDeepEqual),
    );
    this.visibleHeaderActions$ = this.elementDataActions.getVisibleComponentActions(
      this.runtimeContext$,
      this.elementId,
      headerActions$,
    );

    this.initActionStyles(
      this.elementActionsKey,
      headerActions$.pipe(map((actions) => actions ?? [])),
      ['header-action-btn'],
    );

    this.getElementUiFeatures<ContainerElementMetaUiFeatures>()
      .pipe(untilDestroyed(this))
      .subscribe(({ title }) => {
        this.labelText = title ?? '';
        safeDetectChanges(this.cdr);
      });
    this.dataValue.dataInput$.pipe(untilDestroyed(this)).subscribe((input) => {
      if (!input) {
        return;
      }
      const { data, updateSource } = input;

      if (
        !this.valueProps.initialized ||
        (this.valueProps.preInitValues && Object.keys(this.valueProps.preInitValues).length)
      ) {
        this.value = { ...data, ...this.valueProps.preInitValues };
        this.valueProps = { ...this.valueProps, preInitValues: undefined };
      } else {
        this.value = { ...data };
      }

      if (input.schema) {
        this.validateFormValue(true);
      }

      const resolvedUpdateSource =
        updateSource && this.value
          ? updateSource
          : this.valueProps.initialized
            ? 'provider'
            : 'initial';

      this.updateOutput(resolvedUpdateSource);

      if (!this.valueProps.initialized) {
        this.valueProps = { initialized: true, preInitValues: undefined };
      }
    });

    this.actions$
      .pipe(
        ofType(runtimeActions.formValidationTrigger),
        filter(({ formElementId }) => formElementId === this.elementId),
        debounceTime(250),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.validateFormValue(true);
      });
  }

  ngOnDestroy(): void {
    this.formStateService.clear(this.elementId);
  }

  getInstanceValue(): DatasetRow | undefined {
    return this.value;
  }

  valueUpdated(field: string, value: any): void {
    if (!this.valueProps.initialized) {
      if (!this.valueProps.preInitValues) {
        this.valueProps.preInitValues = {};
      }
      this.valueProps = {
        ...this.valueProps,
        preInitValues: {
          ...this.valueProps.preInitValues,
          [field]: value,
        },
      };
    }
    this.outputEmitTracker.shouldEmit(this.value);
  }

  validateValue(): void {
    this.validateFormValue(false);
  }

  shouldEmitOutput(): boolean {
    return this.outputEmitTracker.hasChanges();
  }

  protected onHeaderAction(action: RuntimeContainerActionModel, event: Event): void {
    this.elementDataActions.onAction({
      event,
      action,
      dataRow: this.value,
      output: this.output,
      dataRowSchema: this.schema,
    });
  }

  private setupScrollToError(): void {
    let scrollToErrorSub: Subscription | undefined = undefined;
    effect(() => {
      const scrollToError = this.scrollToError();
      if (scrollToErrorSub) {
        scrollToErrorSub.unsubscribe();
        scrollToErrorSub = undefined;
      }
      if (scrollToError) {
        scrollToErrorSub = this.actions$
          .pipe(
            ofType(runtimeActions.updateFormSubmitted),
            filter(({ formElementId }) => formElementId === this.elementId),
            untilDestroyed(this),
          )
          .subscribe(() => {
            const firstError = Object.entries(this.elementErrors)[0];
            if (!arePropsWithWrapperRefs(this.props) || !firstError) {
              return;
            }
            const errorElement = findByRefIds(
              this.props.wrapper?.refs,
              WrapperField.getRefIds(firstError[1]),
            );
            setTimeout(() => {
              const errElement = document.querySelector(
                `.blaze-element[data-id="${errorElement}"]`,
              );
              errElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
          });
      }
    });
  }

  private validateFormValue(forceValidation = false): void {
    if (
      this.elementType !== 'formContainer' ||
      (!forceValidation && !this.validateEmitTracker.shouldEmit(this.value)) ||
      !this.value
    ) {
      return;
    }

    const customFields = getFormElementCustomFields(this.props?.content.model)?.map((f) => f.field);
    const blankValue = this.rowGenerationService.getNewRow(
      getActiveBinding(this.props?.data, 'value')?.dataSource,
      { customFields },
    );
    this.elementErrors = this.formValidation.validateForm(
      this.elementId,
      this.schema,
      this.value,
      blankValue,
    );
    this.validationReady = true;
  }

  private updateStyles(): void {
    const { style } = this.props ?? {};
    const { container, titleBlock } = style || {};
    if (!container || !titleBlock) {
      return;
    }

    const headerStyles: Record<string, string | undefined> = {
      marginBottom: this.isUsingOldPaddingStrategy ? '8px' : '16px',
      padding: this.isUsingOldPaddingStrategy ? '0 16px' : undefined,
    };

    const wrapperStyles = {
      padding: container.offset?.padding,
      background: getBackground(container.fill),
      ...makeBorderStyles(container.outline, container.outlineWidth),
      borderRadius: container.radius !== '0px' ? container.radius : undefined,
      ...makeContainerVisualEffectStyles(container.visualEffect),
    };

    const slotStyles = {
      padding: this.isUsingOldPaddingStrategy ? '16px' : undefined,
    };
    const noDataStyles = {
      ['min-height']: `${container.noData.minHeight}px`,
    };

    this.headerStyles = headerStyles;
    this.wrapperStyles = wrapperStyles;
    this.slotStyles = slotStyles;
    this.noDataStyles = noDataStyles;
  }

  private updateSchemas(valueSchema: BlazeSchema | undefined = undefined): void {
    if (!this.elementId) {
      return;
    }

    let schema = valueSchema;
    if (this.elementType === 'formContainer' && this.props?.content?.action === 'custom') {
      schema = handleCustomFieldsSchema(
        valueSchema,
        getFormElementCustomFields(this.props?.content.model),
      );
    }

    this.setDefaultEventSchema('action', this.createElementSchema('action', undefined, schema));
    this.setDefaultEventSchema('change', this.createElementSchema('change', undefined, schema));
  }

  private updateOutput(updateSource: ElementOutputUpdateSource): void {
    const isEmptyValue = isEmptyish(this.value);
    if (
      this.elementType === 'formDetails' &&
      this.bindingResults?.['value'] &&
      this.props?.content.origin !== 'wizard'
    ) {
      this.showNoDataPanel = isEmptyValue;
      safeDetectChanges(this.cdr);
    }

    if (!this.outputEmitTracker.shouldEmit(this.value)) {
      return;
    }

    this.output.emit({
      eventType: 'change',
      data: this.value,
      isInitialData: false,
      schema: this.schema,
      updateSource,
    });
  }
}
